import { useState, useCallback, useRef } from 'react';
import { RelatedSection } from './RelatedSection';
import type { ProductListing, RelationType, RelatedSectionConfig } from '../../types/products';

interface GroupedRelatedProductsProps {
  groups: Partial<Record<RelationType, ProductListing[]>>;
  sections?: RelatedSectionConfig[] | null;
  onAddToCart: (productId: number, formatId: number, quantity: number) => Promise<void>;
  isEditMode?: boolean;
  onOrderChange?: (order: Record<string, number[]>) => void;
}

const DEFAULT_TYPE_CONFIG: RelatedSectionConfig[] = [
  { type: 'alternative', title: 'Alternativas', icon: 'fa-arrows-left-right', max_items: 4 },
  { type: 'variety', title: 'Variedades', icon: 'fa-palette', max_items: 4 },
  { type: 'complementary', title: 'Para acompañar', icon: 'fa-utensils', max_items: 4 },
  { type: 'upgrade', title: 'Upgrade', icon: 'fa-arrow-up', max_items: 4 },
  { type: 'healthy_swap', title: 'Opción saludable', icon: 'fa-heart', max_items: 4 },
];

export function GroupedRelatedProducts({ groups, sections, onAddToCart, isEditMode, onOrderChange }: GroupedRelatedProductsProps) {
  const config = sections && sections.length > 0 ? sections : DEFAULT_TYPE_CONFIG;

  const hasAny = config.some(({ type }) => groups[type] && groups[type]!.length > 0);
  if (!hasAny) return null;

  // Track local order per type for edit mode
  const [localOrders, setLocalOrders] = useState<Record<string, number[]>>({});

  const handleReorder = useCallback((type: string, orderedIds: number[]) => {
    setLocalOrders(prev => {
      const next = { ...prev, [type]: orderedIds };
      // Build full order for all types
      const fullOrder: Record<string, number[]> = {};
      for (const s of config) {
        const products = groups[s.type];
        if (!products || products.length === 0) continue;
        fullOrder[s.type] = next[s.type] || products.map(p => p.id);
      }
      onOrderChange?.(fullOrder);
      return next;
    });
  }, [config, groups, onOrderChange]);

  return (
    <section className="mt-12">
      <h2 className="mb-6 text-2xl font-medium text-gray-800">
        Productos Relacionados
      </h2>
      {config.map(({ type, title, icon, max_items }) => {
        const products = groups[type];
        if (!products || products.length === 0) return null;

        // Apply local reorder if exists
        const orderedProducts = localOrders[type]
          ? localOrders[type]
              .map(id => products.find(p => p.id === id))
              .filter((p): p is ProductListing => p != null)
              // Append any products not in the order (newly added)
              .concat(products.filter(p => !localOrders[type].includes(p.id)))
          : products;

        return (
          <RelatedSection
            key={type}
            type={type}
            title={title}
            icon={icon}
            products={orderedProducts}
            maxItems={max_items ?? 4}
            onAddToCart={onAddToCart}
            isEditMode={isEditMode}
            onReorder={(ids) => handleReorder(type, ids)}
          />
        );
      })}
    </section>
  );
}
